import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';

// 애니메이션 및 스크롤바 스타일
const style = document.createElement('style');
style.innerHTML = `
@keyframes bell-ring {
  0% { transform: rotate(0); }
  10% { transform: rotate(15deg); }
  20% { transform: rotate(-10deg); }
  30% { transform: rotate(5deg); }
  40% { transform: rotate(-5deg); }
  50% { transform: rotate(0); }
  100% { transform: rotate(0); }
}
.bell-ringing {
  animation: bell-ring 2s ease-in-out infinite;
  display: inline-block;
}
.noti-item:hover .noti-close-btn {
  display: flex !important;
}
.noti-scroll::-webkit-scrollbar {
  width: 6px;
}
.noti-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.noti-scroll::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}
.noti-preview-scroll div > p:first-child {
  margin-top: 0 !important;
}
.noti-preview-scroll div > p:last-child {
  margin-bottom: 0 !important;
}
`;
document.head.appendChild(style);

// 모듈별 뱃지 스타일 매핑 함수 (메뉴별 고유 색상 구분)
const getModuleBadgeStyle = (moduleName, moduleType) => {
  const target = moduleName || moduleType;
  switch (target) {
    case '업무협조':
    case '업무지시':
    case 'work_order':
      return { bg: '#2563eb', color: '#fff' }; // 파란색 (업무협조)
    case '프로젝트':
    case 'pms':
      return { bg: '#f57c00', color: '#fff' }; // 주황색 (프로젝트)
    case '전자결재':
    case 'approval':
      return { bg: '#10b981', color: '#fff' }; // 초록색 (전자결재)
    case '일정':
    case 'calendar':
      return { bg: '#8b5cf6', color: '#fff' }; // 보라색 (일정)
    default:
      return { bg: '#64748b', color: '#fff' }; // 슬레이트 (기타)
  }
};

function NotificationBell() {
  const { unreadCount, decrementUnread, markAllReadContext } = useNotification();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest('.noti-preview-portal')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setPreviewData(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    try {
      const token = sessionStorage.getItem('token');
      // 최근 30개 알림 로드 (읽음 포함)
      const res = await fetch('/api/notifications?page=1&limit=30', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.data);
      }
    } catch (err) {
      console.error('알림 목록 불러오기 실패', err);
    }
  };

  const handleRead = async (noti, e) => {
    // X 버튼 클릭 이벤트 전파 방지
    if (e) e.stopPropagation();

    if (!noti.is_read) {
      try {
        const token = sessionStorage.getItem('token');
        await fetch(`/api/notifications/${noti.id}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
        decrementUnread();
        setNotifications(prev => prev.map(n => n.id === noti.id ? { ...n, is_read: true } : n));
      } catch (err) {
        console.error('읽음 처리 실패', err);
      }
    }
  };

  const handleItemClick = async (noti) => {
    const token = sessionStorage.getItem('token');

    // [사전 검사] PMS 원본 피드 존재 여부 확인 (PMS 모듈 전용)
    let feedIdToCheck = null;
    let targetUrl = noti.url;

    if (noti.module_type === 'pms' && noti.action_type === 'feed_create') {
      try {
        const extra = typeof noti.extra_data === 'string' ? JSON.parse(noti.extra_data) : (noti.extra_data || {});
        feedIdToCheck = extra.feedId;

        if (!targetUrl.includes('tab=')) {
          targetUrl += (targetUrl.includes('?') ? '&' : '?') + 'tab=comments';
        }
        if (feedIdToCheck && !targetUrl.includes('focusId=')) {
          targetUrl += `&focusId=${feedIdToCheck}`;
        }
      } catch (e) { }

      if (feedIdToCheck) {
        try {
          const checkRes = await fetch(`/api/pms/feedbacks/${feedIdToCheck}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!checkRes.ok && checkRes.status === 404) {
            // 글이 없음(삭제됨)
            setIsOpen(false);
            if (window.confirm('이미 삭제된 게시글입니다.\n이 알림 내역을 삭제하시겠습니까?')) {
              await fetch(`/api/notifications/${noti.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
              });
              if (!noti.is_read) {
                decrementUnread();
              }
              // 로컬 목록에서도 삭제
              setNotifications(prev => prev.filter(n => n.id !== noti.id));
            }
            return; // 이동 차단
          }
        } catch (err) {
          console.error('원본 글 존재 여부 확인 실패', err);
        }
      }
    }

    // 정상인 경우
    handleRead(noti);
    setIsOpen(false);
    if (targetUrl) {
      // 동일한 알림을 여러 번 클릭해도 항상 새로운 이동으로 인식되도록 내부 state(타임스탬프) 전달
      navigate(targetUrl, { state: { notificationTrigger: Date.now() } });
    }
  };

  const handleReadAll = async () => {
    try {
      const token = sessionStorage.getItem('token');
      await fetch('/api/notifications/read-all', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      markAllReadContext();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('전체 읽음 처리 실패', err);
    }
  };

  const handleDeleteRead = async () => {
    try {
      const token = sessionStorage.getItem('token');
      await fetch('/api/notifications/read', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      // 읽은 알림만 로컬 상태에서 제거
      setNotifications(prev => prev.filter(n => !n.is_read));
    } catch (err) {
      console.error('읽은 알림 삭제 실패', err);
    }
  };

  const hasReadNotifications = notifications.some(n => n.is_read);

  return (
    <div className="position-relative me-0" ref={dropdownRef}>
      {/* 종 아이콘 (테두리 없음, 기존처럼 깔끔하게) */}
      <button
        className="btn btn-link text-secondary text-decoration-none position-relative border-0 shadow-none p-1"
        onClick={() => setIsOpen(!isOpen)}
        title="새로운 알림"
      >
        <i
          className={`fas fa-bell ${unreadCount > 0 ? 'bell-ringing' : ''}`}
          style={{ fontSize: '1.4rem', color: unreadCount > 0 ? '#ffc107' : '#6c757d' }}
        ></i>
        {unreadCount > 0 && (
          <span
            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
            style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="dropdown-menu dropdown-menu-end show p-0"
          style={{
            position: 'absolute', right: 0, top: '45px', width: '360px',
            borderRadius: '12px', overflow: 'hidden', zIndex: 1050,
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 12px 36px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)'
          }}
        >
          {/* 상단 헤더 (배경색 진하게, 높이 살짝 늘림) */}
          <div className="d-flex justify-content-between align-items-center px-3" style={{ padding: '16px 12px', backgroundColor: '#495057', borderBottom: '1px solid #343a40' }}>
            <h6 className="m-0 font-weight-bold" style={{ fontSize: '13px', color: '#ffffff' }}>
              <i className="fas fa-bell me-1" style={{ color: '#ffb300' }}></i> 최근 알림
            </h6>
            <div className="d-flex align-items-center" style={{ gap: '10px' }}>
              {hasReadNotifications && (
                <span
                  style={{ fontSize: '11px', fontWeight: '600', color: '#ff6b6b', cursor: 'pointer', letterSpacing: '-0.3px' }}
                  onClick={handleDeleteRead}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  읽은알림삭제
                </span>
              )}
              {unreadCount > 0 && (
                <span
                  style={{ fontSize: '11px', fontWeight: '600', color: '#ced4da', cursor: 'pointer', letterSpacing: '-0.3px' }}
                  onClick={handleReadAll}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  모두 읽음
                </span>
              )}
            </div>
          </div>

          {/* 알림 리스트 */}
          <div className="list-group list-group-flush noti-scroll" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>
                새로운 알림이 없습니다.
              </div>
            ) : (
              notifications.map((n) => {
                let extra = {};
                try {
                  extra = typeof n.extra_data === 'string' ? JSON.parse(n.extra_data) : (n.extra_data || {});
                } catch (e) { }

                const isUnread = !n.is_read;

                // 연도+날짜+시간 포맷 (예: 2026.07.08 15:30)
                const d = new Date(n.createdAt);
                const year = d.getFullYear();
                const timeStr = `${year}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

                return (
                  <div
                    key={n.id}
                    className={`list-group-item list-group-item-action p-2 noti-item position-relative`}
                    onClick={() => handleItemClick(n)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isUnread ? '#eef5ff' : '#fff',
                      opacity: isUnread ? 1 : 0.78,
                      borderBottom: '1px solid #e0e0e0',
                      borderTop: 'none',
                      transition: 'background-color 0.2s ease, opacity 0.2s ease'
                    }}
                  >
                    {/* 우측 상단 액션 버튼 그룹 (호버 시 표시) - 캡슐형 */}
                    <div
                      className="noti-close-btn position-absolute"
                      style={{
                        top: '8px', right: '8px', display: 'none', zIndex: 10,
                        gap: '2px', backgroundColor: 'rgba(255,255,255,0.95)',
                        borderRadius: '12px', padding: '2px 3px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #eee'
                      }}
                    >
                      {/* 내용 미리보기 버튼 */}
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: '22px', height: '22px', borderRadius: '50%',
                          color: '#1976d2', cursor: 'pointer', transition: 'background-color 0.15s'
                        }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (previewData && previewData.noti.id === n.id) {
                            setPreviewData(null);
                            return;
                          }
                          const rect = e.currentTarget.getBoundingClientRect();
                          setPreviewData({
                            noti: n,
                            pos: { top: rect.bottom + 5, right: window.innerWidth - rect.right },
                            content: null,
                            loading: true,
                            extra,
                            timeStr
                          });
                          try {
                            const token = sessionStorage.getItem('token');
                            if (n.module_type === 'work_order') {
                              // 💡 업무지시 피드/코멘트/파일 미리보기
                              if (extra.fileNames && Array.isArray(extra.fileNames) && extra.fileNames.length > 0) {
                                const fileListHtml = `<strong>[첨부파일 ${extra.fileNames.length}건]</strong><br/>` + extra.fileNames.map(f => `• ${f}`).join('<br/>') + (extra.feedContent && !extra.feedContent.startsWith('[첨부파일]') ? `<br/><br/>${extra.feedContent}` : '');
                                setPreviewData(prev => prev && prev.noti.id === n.id ? { ...prev, content: { data: { content: fileListHtml } }, loading: false } : prev);
                              } else if (extra.feedContent) {
                                setPreviewData(prev => prev && prev.noti.id === n.id ? { ...prev, content: { data: { content: extra.feedContent } }, loading: false } : prev);
                              } else {
                                const targetWorkOrderId = extra.workOrderId || n.ref_id;
                                if (targetWorkOrderId) {
                                  const res = await fetch(`/api/work-orders/${targetWorkOrderId}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    const woData = data.data || {};
                                    let displayContent = '';
                                    if (extra.feedId) {
                                      const targetComment = (woData.comments || []).find(c => c.id === Number(extra.feedId));
                                      if (targetComment?.content) {
                                        displayContent = targetComment.content;
                                      }
                                    }
                                    // 피드 내용이 없거나 업무 단계 알림인 경우 기본 내용(지시 내용/제목) 표출
                                    if (!displayContent) {
                                      displayContent = woData.content || (woData.title ? `<strong>${woData.title}</strong>` : n.message);
                                    }
                                    setPreviewData(prev => prev && prev.noti.id === n.id ? {
                                      ...prev,
                                      content: { data: { content: displayContent } },
                                      loading: false
                                    } : prev);
                                  } else {
                                    setPreviewData(prev => prev && prev.noti.id === n.id ? {
                                      ...prev,
                                      content: { data: { content: extra.orderTitle ? `<strong>${extra.orderTitle}</strong>` : n.message } },
                                      loading: false
                                    } : prev);
                                  }
                                } else {
                                  setPreviewData(prev => prev && prev.noti.id === n.id ? {
                                    ...prev,
                                    content: { data: { content: extra.orderTitle ? `<strong>${extra.orderTitle}</strong>` : n.message } },
                                    loading: false
                                  } : prev);
                                }
                              }
                            } else if (extra.feedId) {
                              // 🔒 기존 PMS 피드백 로직 (feedId가 있는 경우만 조회)
                              const res = await fetch(`/api/pms/feedbacks/${extra.feedId}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (res.ok) {
                                const data = await res.json();
                                setPreviewData(prev => prev && prev.noti.id === n.id ? { ...prev, content: data, loading: false } : prev);
                              } else if (res.status === 404) {
                                // 원본 삭제됨: 미리보기 닫고 삭제 로직 수행
                                setPreviewData(null);
                                if (window.confirm('이미 삭제된 게시글입니다.\n이 알림 내역을 삭제하시겠습니까?')) {
                                  await fetch(`/api/notifications/${n.id}`, {
                                    method: 'DELETE',
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  if (!n.is_read) decrementUnread();
                                  setNotifications(prev => prev.filter(item => item.id !== n.id));
                                }
                              } else {
                                setPreviewData(prev => prev && prev.noti.id === n.id ? { ...prev, content: null, loading: false } : prev);
                              }
                            } else {
                              // 세부업무 코멘트/배정/파일/마감일 등은 메시지 본문을 미리보기에 즉시 표시 (404 오류 방지)
                              setPreviewData(prev => prev && prev.noti.id === n.id ? {
                                ...prev,
                                content: { data: { content: extra.taskTitle ? `<strong>[${extra.taskTitle}]</strong><br/>${n.message}` : n.message } },
                                loading: false
                              } : prev);
                            }
                          } catch (err) {
                            setPreviewData(prev => prev && prev.noti.id === n.id ? { ...prev, content: null, loading: false } : prev);
                          }
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="내용 확인"
                      >
                        <i className="fas fa-eye" style={{ fontSize: '10px' }}></i>
                      </div>

                      {/* 개별 읽음 처리 버튼 (안 읽은 알림일 때만) */}
                      {isUnread && (
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: '22px', height: '22px', borderRadius: '50%',
                            color: '#28a745', cursor: 'pointer', transition: 'background-color 0.15s'
                          }}
                          onClick={(e) => handleRead(n, e)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8f5e9'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          title="읽음 처리"
                        >
                          <i className="fas fa-check" style={{ fontSize: '10px' }}></i>
                        </div>
                      )}

                      {/* 삭제 버튼 (항상) */}
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: '22px', height: '22px', borderRadius: '50%',
                          color: '#dc3545', cursor: 'pointer', transition: 'background-color 0.15s'
                        }}
                        onClick={async (e) => {
                          e.stopPropagation(); // 라우팅 방지
                          try {
                            const token = sessionStorage.getItem('token');
                            await fetch(`/api/notifications/${n.id}`, {
                              method: 'DELETE',
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setNotifications(prev => prev.filter(item => item.id !== n.id));
                            if (!n.is_read) decrementUnread();
                          } catch (err) {
                            console.error('삭제 실패', err);
                          }
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fdecea'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="완전 삭제"
                      >
                        <i className="fas fa-trash-alt" style={{ fontSize: '10px' }}></i>
                      </div>
                    </div>

                    <div className="d-flex flex-column" style={{ gap: '4px' }}>
                      {/* 팝오버는 Portal을 통해 body의 가장 바깥쪽에 렌더링 */}
                      {previewData && previewData.noti.id === n.id && createPortal(
                        <div
                          className="noti-preview-portal"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'fixed',
                            top: `${previewData.pos.top}px`,
                            right: `${previewData.pos.right}px`,
                            width: '340px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                            border: '1px solid #e2e8f0',
                            zIndex: 9999,
                            overflow: 'hidden'
                          }}
                        >
                          {/* 닫기 버튼 (절대 위치: 맨위 오른쪽 모서리 끝) */}
                          <div
                            onClick={(e) => { e.stopPropagation(); setPreviewData(null); }}
                            style={{ position: 'absolute', top: '2px', right: '4px', cursor: 'pointer', color: '#94a3b8', fontSize: '10px', zIndex: 10, padding: '2px' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#334155'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                          >
                            <i className="fas fa-times"></i>
                          </div>

                          {/* 상단 타이틀 바 (상단 여백을 주어 X버튼과 안 겹치게, 이동버튼 우측 끝) */}
                          <div className="d-flex justify-content-between align-items-end" style={{ backgroundColor: '#f8fafc', padding: '16px 16px 8px 16px', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#334155' }}>
                              <i className="fas fa-eye me-1" style={{ color: '#3b82f6' }}></i> 내용 확인
                            </span>
                            {/* 피드로 이동 */}
                            {previewData.noti.url && (
                              <span
                                style={{ fontSize: '11px', color: '#2563eb', cursor: 'pointer', fontWeight: '600' }}
                                onClick={() => { setPreviewData(null); handleItemClick(previewData.noti); }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                              >
                                피드로 이동 →
                              </span>
                            )}
                          </div>

                          {/* 본문 영역 (패딩 줄여서 콤팩트하게) */}
                          <div style={{ padding: '12px' }}>
                            <div className="noti-preview-scroll" style={{
                              fontSize: '12px', color: '#334155', lineHeight: '1.4',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap',
                              maxHeight: '5.3em',
                              overflowY: 'auto',
                              backgroundColor: '#ffffff',
                              border: '1px solid #cbd5e1',
                              borderRadius: '4px',
                              padding: '6px 10px'
                            }}>
                              {previewData.loading ? (
                                <div className="text-center py-2"><i className="fas fa-spinner fa-spin text-muted"></i></div>
                              ) : (
                                <div dangerouslySetInnerHTML={{ __html: previewData.content?.data?.content || '<span class="text-muted">내용을 불러올 수 없습니다.</span>' }} />
                              )}
                            </div>
                          </div>
                        </div>,
                        document.body
                      )}
                      {/* 첫 번째 줄: 배지 영역 */}
                      {(extra.moduleName || extra.projectName || extra.orderTitle) && (
                        <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                          {extra.moduleName && (() => {
                            const badgeStyle = getModuleBadgeStyle(extra.moduleName, n.module_type);
                            return (
                              <span style={{
                                backgroundColor: badgeStyle.bg, color: badgeStyle.color,
                                padding: '1px 5px', borderRadius: '3px', fontSize: '10px', fontWeight: 'bold'
                              }}>
                                {extra.moduleName}
                              </span>
                            );
                          })()}
                          {(extra.projectName || extra.orderTitle) && (
                            <span style={{
                              backgroundColor: '#e6f0ff', color: '#3ab0ff',
                              padding: '1px 5px', borderRadius: '3px', fontSize: '10px', fontWeight: 'bold'
                            }}>
                              {extra.projectName || extra.orderTitle}
                            </span>
                          )}
                        </div>
                      )}

                      {/* 두 번째 줄: 메시지 본문 + 날짜/시간 */}
                      <div className="d-flex justify-content-between align-items-start">
                        <span
                          className="text-break pe-3"
                          style={{
                            fontSize: '11.5px',
                            color: isUnread ? '#1a1a1a' : '#777',
                            fontWeight: isUnread ? '600' : 'normal',
                            lineHeight: '1.45'
                          }}
                        >
                          {n.message}
                        </span>
                        <small style={{ color: '#999', fontSize: '10px', whiteSpace: 'nowrap', marginTop: '2px' }}>
                          {timeStr}
                        </small>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
